"use client";
import React, { useRef,forwardRef, useState, useEffect, useImperativeHandle } from "react";
import { Check, CircleX, FilePlus2, FileText, Filter, MoreVertical } from "lucide-react";
import { saveAs } from "file-saver";
import { Action, TypeFile } from "@/app/constants/enums";
import { iMyDocFileData } from "@/app/models/interfaceMyDocFileData";
import {
  chatbotMyDocsFileDataDelete,
  chatbotMyDocsFileDataDowload,
} from "@/app/services/chatbotMyDocsFileData";
import { getExtensionFromMimeType } from "@/app/helpers/getExtensionFromMimeType";
import InputChat from "../../Elements/Core/InputChat";
import ButtonChat from "../../Elements/Core/ButtonChat";
import Tooltip from "../../Elements/Tooltip";
import Loading from "../../Elements/Loading";
import UploadModal from "../../UploadModal/UploadModal";
import { useUser } from "@/app/context/UserContext";

interface MySourcesListProps {
  handleSelectMyDocs: (checked: boolean) => void;
}

export interface MySourcesListRef {
  handleAdd: () => void;
}

const MySourcesList = forwardRef<MySourcesListRef, MySourcesListProps>(
  (props, ref) => {
    const {
        user,setUser, 
        userProject,  
        myDocs,setMyDocs, myDocsLoading, 
      } = useUser();
  const [isModalOpenFile, setIsModalOpenFile] = useState(false);
  const [myDocFileData, setMyDocFileData] = useState<iMyDocFileData | null>(
    null
  );
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const [loadingDownload, setLoadingDownload] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [deleted, setDeleted] = useState(0);
  const [download, setDownload] = useState(0);

  const toggleMenu = (fileId: string) => {
    setActiveMenu((prev) => (prev === fileId ? null : fileId));
    setLoadingDownload(false);
    setDownload(0);
    setDeleted(0);
  };

 

  const handleEdit = (doc: any) => {
    setMyDocFileData(doc);
    setIsModalOpenFile(true);
  };

  const handleDownload = async (doc: any) => {
    try {
      setLoadingDownload(true);
      const response = await chatbotMyDocsFileDataDowload(doc, user, setUser);
      if (response.success && response.code === 200) {
        if (response.data instanceof Blob) {
          saveAs(
            response.data,
            `${doc.name.substring(150)}.${getExtensionFromMimeType(
              response.data.type
            )}`
          );
          setDownload(1);
        } else {
          setDownload(2);
        }
      } else {
        setDownload(2);
      }
    } catch (error) {
      console.error("Error al descargar el archivo:", error);
      setDownload(2);
    } finally {
      setLoadingDownload(false);
    }
  };

  const handleDelete = async (doc: any) => {
    try {
      setLoadingDelete(true);
      const response = await chatbotMyDocsFileDataDelete(doc, user, setUser);
      if (response.success && response.code === 200) {
        const updatedDocs = myDocs.filter((item) => item.file_id !== doc.file_id);
        setDeleted(1);
        setTimeout(() => setMyDocs(updatedDocs), 1000);
      } else {
        setDeleted(2);
      }
    } catch (error) {
      console.error("Error al eliminar el archivo:", error);
      setDeleted(2);
    } finally {
      setLoadingDelete(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setActiveMenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);


  // exponer metodo 
  useImperativeHandle(ref, () => ({
      handleAdd: () => {
        setMyDocFileData(null);
        setIsModalOpenFile(true);
      },
    }));

  return (
    <div> 
      {user && !user.isAnonymous && (
        myDocsLoading ? (
          "loading..."
        ) : (
            <ul className="space-y-2">
               { 
               myDocs?.filter(O=>O.name).map((doc: any) => (
                    <li
                        key={doc.file_id}
                        className={`flex justify-between items-center cursor-pointer${ doc.filename ? "" : "hidden"}`}
                    > 
                        <div className="flex items-center max-w-[80%] space-x-2" onClick={() => toggleMenu(doc.file_id)}> 
                            <FileText
                                strokeWidth={1}
                                className={`text-${doc.type === TypeFile.Pdf ? "red" : "green"}-500 w-6 h-6 flex-shrink-0`}
                            /> 
                            <span className="truncate hover:text-blue-700">
                                <Tooltip text={doc.name}>{doc.name}</Tooltip>
                            </span>
                        </div>

                        <div className="flex items-rigth max-w-[20%]">
                            <div
                                className="me-7"
                                onMouseDown={(e) => e.stopPropagation()}
                            >
                                <ButtonChat
                                    actionCode={Action.LEFT_MY_DOC_MENU}
                                    className="absolute rounded-full text-blue-700 hover:bg-slate-200 -mt-5 "
                                    onClick={() => toggleMenu(doc.file_id)}
                                >
                                    <MoreVertical strokeWidth={1.5} className="m-2" />
                                </ButtonChat>
                                {activeMenu === doc.file_id && (
                                    <div
                                    key={doc.file_id + "menu"}
                                    ref={menuRef}
                                    className={`
                                        absolute right-0 mt-5 w-40 bg-white border border-gray-300 rounded-lg shadow-lg z-10
                                        transition-all duration-300 ease-in-out
                                        ${activeMenu !== doc.file_id ? "animate-fadeOut" : "animate-fadeIn"}
                                        `}
                                    >
                                    <ButtonChat
                                        actionCode={Action.LEFT_MY_DOC_EDIT}
                                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                        onClick={(e) => {
                                        e.stopPropagation();
                                        handleEdit(doc);
                                        }}
                                    >
                                        Editar
                                    </ButtonChat>
                                    <ButtonChat
                                        actionCode={Action.LEFT_MY_DOC_DOWNLOAD}
                                        className="flex justify-between w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                        disabled={download !== 0}
                                        onClick={(e) => {
                                        e.stopPropagation();
                                        handleDownload(doc);
                                        }}
                                    >
                                        Descargar
                                        {loadingDownload && <Loading w="w-2" h="h-2" className="ml-auto" />}
                                        {download === 1 && <Check strokeWidth={0.8} />}
                                        {download === 2 && <CircleX strokeWidth={0.8} />}
                                    </ButtonChat>
                                    <ButtonChat
                                        actionCode={Action.LEFT_MY_DOC_DELETE}
                                        className="flex justify-between w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                        disabled={deleted !== 0}
                                        onClick={(e) => {
                                        e.stopPropagation();
                                        handleDelete(doc);
                                        }}
                                    >
                                        Eliminar
                                        {loadingDelete && <Loading w="w-2" h="h-2" className="ml-auto" />}
                                        {deleted === 1 && <Check strokeWidth={0.8} />}
                                        {deleted === 2 && <CircleX strokeWidth={0.8} />}
                                    </ButtonChat>
                                    </div>
                                )}

                            </div>
                        </div>
                            
                            
                    </li>
                ))
                
                }
            </ul>        
        )
        
      )
      }

      {isModalOpenFile && (
        <UploadModal
          isOpen={isModalOpenFile}
          onClose={() => setIsModalOpenFile(false)}
          myDoc={myDocFileData}
        />
      )}
    </div>
  );
}

);

export default MySourcesList;
